import type { ArtifactInfo } from '../types/agent'

export interface ParsedChartRow {
  label: string
  values: number[]
}

export interface ParsedChartData {
  headers: string[]
  rows: ParsedChartRow[]
}

const IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.svg', '.gif', '.webp'])

/**
 * Check if an artifact should be rendered as an inline image.
 * Returns true for image/chart artifact types, or file artifacts with image extensions.
 */
export function isImageArtifact(artifact: ArtifactInfo): boolean {
  if (artifact.artifactType === 'image' || artifact.artifactType === 'chart') {
    return true
  }
  if (artifact.artifactType === 'file') {
    const ext = '.' + artifact.fileName.split('.').pop()?.toLowerCase()
    return IMAGE_EXTENSIONS.has(ext)
  }
  return false
}

/**
 * Build the URL to load an artifact image.
 * - Live artifacts: proxy via workspace files endpoint
 * - Extracted artifacts with id: download from Azure via artifact endpoint
 * - Lost artifacts: return null
 */
export function getArtifactImageUrl(
  artifact: ArtifactInfo,
  _sessionId: string,
  baseUrl: string,
): string | null {
  if (artifact.status === 'lost') return null
  if (artifact.id) {
    return `${baseUrl}/agent/artifacts/${artifact.id}/download`
  }
  return null
}

/**
 * Parse a markdown table string into structured chart data.
 * Expects standard markdown table format with pipe delimiters.
 * Returns null if the content doesn't contain a valid table with at least 2 data rows.
 */
export function parseMarkdownTable(markdown: string): ParsedChartData | null {
  const lines = markdown.split('\n').filter(l => l.trim().length > 0)

  // Find lines that look like table rows (start with |)
  const tableLines = lines.filter(l => l.trim().startsWith('|'))
  if (tableLines.length < 3) return null // need header + separator + at least 2 data rows

  // Parse header row
  const headerCells = parsePipeRow(tableLines[0])
  if (headerCells.length < 2) return null

  // The first cell in the header is usually empty (row label column)
  const headers = headerCells.slice(1)

  // Parse data rows (skip separator rows like |---|---|)
  const dataRows: ParsedChartRow[] = []
  for (let i = 1; i < tableLines.length; i++) {
    const line = tableLines[i].trim()
    // Skip separator rows
    if (/^\|[\s-:|]+\|$/.test(line)) continue

    const cells = parsePipeRow(line)
    if (cells.length < 2) continue

    const label = cells[0]
    const values = cells.slice(1).map(cell => {
      // Strip % and parse as number
      const cleaned = cell.replace(/%/g, '').trim()
      return parseFloat(cleaned)
    })

    dataRows.push({ label, values })
  }

  if (dataRows.length < 2) return null

  return { headers, rows: dataRows }
}

/**
 * Split a markdown pipe-delimited row into trimmed cell values.
 */
function parsePipeRow(line: string): string[] {
  return line
    .split('|')
    .map(cell => cell.trim())
    .filter((_, i, arr) => i > 0 && i < arr.length - 1) // strip empty first/last from leading/trailing |
}
